import React from "react";
import { useTranslation } from "react-i18next";
import { floorplannerStore } from "../../../../store/floorplannerStore";
import {
  CircleSymbolType,
  PolygonSymbolType,
  SquareSymbolType,
  TriangleSymbolType,
} from "../../../../types/floorplannerTypes";
import {
  getShapePrimitiveEffectiveFloorDistance,
  shapeBaseElevationFromEffectiveFloor,
} from "../../../../features/floorplan3d/symbols/primitives/shapes/shapePrimitiveGeometry";
import {
  getPrimitiveSymbol3DModelKeyForSymbol,
  isPrimitiveTable3DModelKey,
} from "../../../../features/floorplan3d/symbols/registry/primitiveSymbol3DConfig";
import DimensionField from "../components/DimensionField";
import LinkedDimensionsGroup from "../components/LinkedDimensionsGroup";
import ObjectRotationField from "../components/ObjectRotationField";
import RotationField from "../components/RotationField";
import MetricsSection from "../components/MetricsSection";
import View3DControlsGrid from "../components/View3DControlsGrid";
import View3DSection from "../components/View3DSection";
import UnitHeader from "../components/UnitHeader";
import type { PropertyPanelController } from "../types";
import { isSymbolAspectRatioLocked } from "../../../../utils/symbolAspectRatio";
import {
  areNumbersEqual,
  degreesToRadians,
  getCommonDegreesValue,
  getCommonMillimeterValue,
  getCommonValue,
  panelGridFieldClassName,
  panelObjectTitleRowClassName,
  radiansToRoundedDegrees,
  toDisplayLength,
  toWorldFromDisplay,
} from "../propertyPanelUtils";

type ShapeSymbol =
  | SquareSymbolType
  | CircleSymbolType
  | TriangleSymbolType
  | PolygonSymbolType;

type ShapeSectionProps = Pick<PropertyPanelController, "updateGlobalState"> & {
  selectedSquares: SquareSymbolType[];
  displayedSquares: SquareSymbolType[];
  selectedCircles: CircleSymbolType[];
  displayedCircles: CircleSymbolType[];
  selectedTriangles: TriangleSymbolType[];
  displayedTriangles: TriangleSymbolType[];
  selectedPolygons: PolygonSymbolType[];
  displayedPolygons: PolygonSymbolType[];
  is3DView: boolean;
};

const getShape3DHeight = (shape: ShapeSymbol) => shape.threeD?.height ?? 0.05;
const isPrimitiveTableShape = (shape: ShapeSymbol) =>
  isPrimitiveTable3DModelKey(getPrimitiveSymbol3DModelKeyForSymbol(shape));
const getShape3DEffectiveFloorDistance = (shape: ShapeSymbol) =>
  getShapePrimitiveEffectiveFloorDistance(shape);
const getShape3DPitchDegrees = (shape: ShapeSymbol) =>
  radiansToRoundedDegrees(shape.threeD?.rotation3D?.pitch ?? 0);
const getShape3DRollDegrees = (shape: ShapeSymbol) =>
  radiansToRoundedDegrees(shape.threeD?.rotation3D?.roll ?? 0);
const getShape3DPlanRotationDegrees = (shape: ShapeSymbol) =>
  radiansToRoundedDegrees(shape.rotation ?? 0);

const ShapeBlock = <T extends ShapeSymbol>({
  selected,
  displayed,
  fallbackName,
  pluralName,
  widthProperty,
  heightProperty,
  getWidth,
  getHeight,
  updateGlobalState,
  is3DView,
}: {
  selected: T[];
  displayed: T[];
  fallbackName: string;
  pluralName: string;
  widthProperty: string;
  heightProperty: string;
  getWidth: (shape: T) => number | undefined;
  getHeight: (shape: T) => number | undefined;
  updateGlobalState: PropertyPanelController["updateGlobalState"];
  is3DView: boolean;
}) => {
  const { t } = useTranslation();
  const mixed = t("common.mixed");

  return (
  <>
    {displayed.map((shape) => {
      const aspectLocked = isSymbolAspectRatioLocked(shape.lockAspectRatio);
      return (
      <div key={`${widthProperty}-${shape?.id}`}>
        <div className="border-b border-gray-300" />
        <div className="text-sm font-normal">
          <div className={panelObjectTitleRowClassName}>
            <UnitHeader
              title={
                selected.length > 1
                  ? `${selected.length} ${pluralName}`
                  : (shape.name ?? fallbackName)
              }
            />
          </div>
          <MetricsSection>
            <LinkedDimensionsGroup
              locked={aspectLocked}
              onToggleLock={() => {
                const newLockState = !aspectLocked;
                selected.forEach((selectedShape) => {
                  floorplannerStore.updateSymbolProperty(
                    selectedShape.id,
                    "lockAspectRatio",
                    newLockState,
                  );
                  if (newLockState) {
                    const ratio =
                      (getWidth(selectedShape) || 1) /
                      (getHeight(selectedShape) || 1);
                    floorplannerStore.updateSymbolProperty(
                      selectedShape.id,
                      "ratio",
                      ratio,
                    );
                  }
                });
              }}
              widthField={
                <DimensionField
                  label={t("propertyPanel.width")}
                  fieldClassName={panelGridFieldClassName}
                  value={
                    selected.length > 1
                      ? (getCommonMillimeterValue(selected, getWidth) ?? "")
                      : toDisplayLength(getWidth(shape) || 0)
                  }
                  placeholder={
                    selected.length > 1 ? mixed : undefined
                  }
                  onChange={(nextValue) => {
                    const value = parseFloat(nextValue);
                    if (!Number.isFinite(value)) return;
                    updateGlobalState(selected, widthProperty, value);
                    if (aspectLocked) {
                      const ratio =
                        shape.ratio ||
                        (getWidth(shape) || 1) / (getHeight(shape) || 1);
                      updateGlobalState(selected, heightProperty, value / ratio);
                      // Under aspect-ratio lock in 3D, scale the extrusion height
                      // proportionally from the precise stored width.
                      const currentWidth = getWidth(shape) || 0;
                      if (
                        is3DView &&
                        !isPrimitiveTableShape(shape) &&
                        currentWidth > 0
                      ) {
                        const scale = toWorldFromDisplay(value) / currentWidth;
                        updateGlobalState(
                          selected,
                          "symbol3DHeight",
                          toDisplayLength(getShape3DHeight(shape) * scale),
                        );
                      }
                    }
                  }}
                />
              }
              lengthField={
                <DimensionField
                  label={t("propertyPanel.length")}
                  fieldClassName={panelGridFieldClassName}
                  value={
                    selected.length > 1
                      ? (getCommonMillimeterValue(selected, getHeight) ?? "")
                      : toDisplayLength(getHeight(shape) || 0)
                  }
                  placeholder={
                    selected.length > 1 ? mixed : undefined
                  }
                  onChange={(nextValue) => {
                    const value = parseFloat(nextValue);
                    if (!Number.isFinite(value)) return;
                    updateGlobalState(selected, heightProperty, value);
                    if (aspectLocked) {
                      const ratio =
                        shape.ratio ||
                        (getWidth(shape) || 1) / (getHeight(shape) || 1);
                      updateGlobalState(selected, widthProperty, value * ratio);
                      // Under aspect-ratio lock in 3D, scale the extrusion height
                      // proportionally from the precise stored length.
                      const currentLength = getHeight(shape) || 0;
                      if (
                        is3DView &&
                        !isPrimitiveTableShape(shape) &&
                        currentLength > 0
                      ) {
                        const scale =
                          toWorldFromDisplay(value) / currentLength;
                        updateGlobalState(
                          selected,
                          "symbol3DHeight",
                          toDisplayLength(getShape3DHeight(shape) * scale),
                        );
                      }
                    }
                  }}
                />
              }
              heightField={
                is3DView ? (
                  <DimensionField
                    label={t("propertyPanel.height")}
                    fieldClassName={panelGridFieldClassName}
                    min={0}
                    value={
                      selected.length > 1
                        ? (getCommonMillimeterValue(
                            selected,
                            getShape3DHeight,
                          ) ?? "")
                        : toDisplayLength(getShape3DHeight(shape))
                    }
                    placeholder={
                      selected.length > 1 ? mixed : undefined
                    }
                    onChange={(nextValue) => {
                      const value = parseFloat(nextValue);
                      if (!Number.isFinite(value)) return;
                      updateGlobalState(selected, "symbol3DHeight", value);
                      if (
                        aspectLocked &&
                        !isPrimitiveTableShape(shape)
                      ) {
                        // Scale the footprint proportionally from the precise
                        // stored height so small (arrow-step) changes accumulate.
                        const currentHeight = getShape3DHeight(shape);
                        if (currentHeight > 0) {
                          const scale =
                            toWorldFromDisplay(value) / currentHeight;
                          updateGlobalState(
                            selected,
                            widthProperty,
                            toDisplayLength((getWidth(shape) || 0) * scale),
                          );
                          updateGlobalState(
                            selected,
                            heightProperty,
                            toDisplayLength((getHeight(shape) || 0) * scale),
                          );
                        }
                      }
                    }}
                  />
                ) : undefined
              }
              trailingField={
                !is3DView ? (
                  <RotationField
                    value={
                      selected.length > 1
                        ? (getCommonDegreesValue(selected) ?? "")
                        : radiansToRoundedDegrees(shape?.rotation || 0)
                    }
                    placeholder={
                      selected.length > 1 ? mixed : undefined
                    }
                    onChange={(degrees) =>
                      updateGlobalState(
                        selected,
                        "rotation",
                        degreesToRadians(degrees),
                      )
                    }
                  />
                ) : undefined
              }
            />
          </MetricsSection>
          {is3DView && (
            <View3DSection>
              <View3DControlsGrid>
                <DimensionField
                  label={t("propertyPanel.distanceFromFloor")}
                  fieldClassName={panelGridFieldClassName}
                  value={
                    selected.length > 1
                      ? (getCommonMillimeterValue(
                          selected,
                          getShape3DEffectiveFloorDistance,
                        ) ?? "")
                      : toDisplayLength(
                          getShape3DEffectiveFloorDistance(shape),
                        )
                  }
                  placeholder={
                    selected.length > 1 ? mixed : undefined
                  }
                  onChange={(nextValue) => {
                    const effectiveWorld = toWorldFromDisplay(
                      parseFloat(nextValue),
                    );
                    selected.forEach((s) => {
                      const rawBaseElev = shapeBaseElevationFromEffectiveFloor(
                        s,
                        effectiveWorld,
                      );
                      updateGlobalState(
                        [s],
                        "symbol3DBaseElevation",
                        toDisplayLength(rawBaseElev),
                      );
                    });
                  }}
                />
                <ObjectRotationField
                  label={t("propertyPanel.rotateObject")}
                  value={
                    selected.length > 1
                      ? (getCommonValue(
                          selected,
                          getShape3DPlanRotationDegrees,
                          areNumbersEqual,
                        ) ?? "")
                      : getShape3DPlanRotationDegrees(shape)
                  }
                  placeholder={
                    selected.length > 1 ? mixed : undefined
                  }
                  onChange={(nextValue) => {
                    const degrees = parseFloat(nextValue);
                    updateGlobalState(
                      selected,
                      "rotation",
                      degreesToRadians(Number.isFinite(degrees) ? degrees : 0),
                    );
                  }}
                />
                <ObjectRotationField
                  label={t("propertyPanel.turnObject")}
                  value={
                    selected.length > 1
                      ? (getCommonValue(
                          selected,
                          getShape3DPitchDegrees,
                          areNumbersEqual,
                        ) ?? "")
                      : getShape3DPitchDegrees(shape)
                  }
                  placeholder={
                    selected.length > 1 ? mixed : undefined
                  }
                  onChange={(nextValue) =>
                    updateGlobalState(selected, "shape3DPitch", nextValue)
                  }
                />
                <ObjectRotationField
                  label={t("propertyPanel.tiltObject")}
                  value={
                    selected.length > 1
                      ? (getCommonValue(
                          selected,
                          getShape3DRollDegrees,
                          areNumbersEqual,
                        ) ?? "")
                      : getShape3DRollDegrees(shape)
                  }
                  placeholder={
                    selected.length > 1 ? mixed : undefined
                  }
                  onChange={(nextValue) =>
                    updateGlobalState(selected, "shape3DRoll", nextValue)
                  }
                />
              </View3DControlsGrid>
            </View3DSection>
          )}
        </div>
        <div className="border-b border-gray-300" />
      </div>
    );
    })}
  </>
  );
};

const ShapeSection: React.FC<ShapeSectionProps> = ({
  selectedSquares,
  displayedSquares,
  selectedCircles,
  displayedCircles,
  selectedTriangles,
  displayedTriangles,
  selectedPolygons,
  displayedPolygons,
  updateGlobalState,
  is3DView,
}) => {
  const { t } = useTranslation();

  return (
  <>
    <ShapeBlock
      selected={selectedSquares}
      displayed={displayedSquares}
      fallbackName={t("propertyPanel.square")}
      pluralName={t("propertyPanel.squares")}
      widthProperty="squareWidth"
      heightProperty="squareHeight"
      getWidth={(shape) => shape.squareWidth}
      getHeight={(shape) => shape.squareHeight}
      updateGlobalState={updateGlobalState}
      is3DView={is3DView}
    />
    <ShapeBlock
      selected={selectedCircles}
      displayed={displayedCircles}
      fallbackName={t("propertyPanel.circle")}
      pluralName={t("propertyPanel.circles")}
      widthProperty="circleWidth"
      heightProperty="circleHeight"
      getWidth={(shape) => shape.circleWidth}
      getHeight={(shape) => shape.circleHeight}
      updateGlobalState={updateGlobalState}
      is3DView={is3DView}
    />
    <ShapeBlock
      selected={selectedTriangles}
      displayed={displayedTriangles}
      fallbackName={t("propertyPanel.triangle")}
      pluralName={t("propertyPanel.triangles")}
      widthProperty="triangleWidth"
      heightProperty="triangleHeight"
      getWidth={(shape) => shape.triangleWidth}
      getHeight={(shape) => shape.triangleHeight}
      updateGlobalState={updateGlobalState}
      is3DView={is3DView}
    />
    <ShapeBlock
      selected={selectedPolygons}
      displayed={displayedPolygons}
      fallbackName={t("propertyPanel.polygon")}
      pluralName={t("propertyPanel.polygons")}
      widthProperty="polygonWidth"
      heightProperty="polygonHeight"
      getWidth={(shape) => shape.polygonWidth}
      getHeight={(shape) => shape.polygonHeight}
      updateGlobalState={updateGlobalState}
      is3DView={is3DView}
    />
  </>
  );
};

export default ShapeSection;
